import { exportPdfTable, downloadUtf8Csv } from '@/shared/lib/jspdfCyrillic';
import type { PpeNorm, PpeProcurementRow, PpeIssuanceRecord } from '@/entities/ppe/model/ppeSchema';
import { ISSUANCE_EXPORT, POSITION_ALL_LABEL, POSITION_ALL_VALUE, PROCUREMENT_EXPORT } from './constants';
import { PPE_PROFESSION_GROUPS } from './professionGroups';
import { ETKS_RANK_ALL_VALUE, SOUT_CLASS_ALL_VALUE } from './ppeNormReference';

export function buildPositionFilterOptions() {
  return [
    { value: POSITION_ALL_VALUE, label: POSITION_ALL_LABEL },
    ...PPE_PROFESSION_GROUPS.map((p) => ({ value: p, label: p })),
  ];
}

function normMatchesProfessionGroup(normPosition: string, group: string): boolean {
  const n = normPosition.trim().toLowerCase();
  const g = group.trim().toLowerCase();
  if (!g) return true;
  return n.startsWith(g);
}

function rankMatchesFilter(normRank: string | undefined, filterRank: string): boolean {
  if (filterRank === ETKS_RANK_ALL_VALUE) return true;
  const n = (normRank ?? '').trim();
  if (!n) return true;
  return n === filterRank;
}

function classMatchesFilter(normClass: string | undefined, filterClass: string): boolean {
  if (filterClass === SOUT_CLASS_ALL_VALUE) return true;
  const c = (normClass ?? '').trim();
  if (!c) return true;
  return c === filterClass;
}

export function filterNormsBySearchAndPosition(
  allNorms: PpeNorm[],
  freeSearch: string,
  ppeNameSearch: string,
  position: string,
  etksRank: string,
  conditionClass: string,
): PpeNorm[] {
  const q = freeSearch.trim().toLowerCase();
  const ppeQ = ppeNameSearch.trim().toLowerCase();
  return allNorms.filter((row) => {
    if (position !== POSITION_ALL_VALUE && !normMatchesProfessionGroup(row.position, position)) return false;
    if (!rankMatchesFilter(row.etksRank, etksRank)) return false;
    if (!classMatchesFilter(row.conditionClass, conditionClass)) return false;
    if (ppeQ && !row.ppeName.toLowerCase().includes(ppeQ)) return false;
    if (!q) return true;
    const hay =
      `${row.position} ${row.ppeName} ${row.quantity} ${row.period} ${row.etksRank ?? ''} ${row.conditionClass ?? ''}`.toLowerCase();
    return hay.includes(q);
  });
}

export function sumProcurementDeficit(rows: PpeProcurementRow[] | undefined): number {
  return (rows ?? []).reduce((acc, r) => acc + r.deficit, 0);
}

export function exportIssuanceRecordsPdf(rows: PpeIssuanceRecord[]) {
  return exportPdfTable({
    title: ISSUANCE_EXPORT.pdfTitle,
    fileSlug: ISSUANCE_EXPORT.pdfSlug,
    head: [[...ISSUANCE_EXPORT.pdfHead]],
    body: rows.map((r, i) => [i + 1, r.employeeName, r.ppeName, r.quantity, r.issueDate, r.expiryDate, r.serviceLife]),
  });
}

export function exportIssuanceRecordsCsv(rows: PpeIssuanceRecord[]) {
  const data = rows.map((r) => [r.employeeName, r.ppeName, r.quantity, r.issueDate, r.expiryDate, r.serviceLife]);
  downloadUtf8Csv(ISSUANCE_EXPORT.csvFilename, ISSUANCE_EXPORT.csvHeader, data);
}

export function exportProcurementPdf(rows: PpeProcurementRow[]) {
  return exportPdfTable({
    title: PROCUREMENT_EXPORT.pdfTitle,
    fileSlug: PROCUREMENT_EXPORT.pdfSlug,
    head: [[...PROCUREMENT_EXPORT.pdfHead]],
    body: rows.map((r, i) => [
      i + 1,
      r.position,
      r.ppeName,
      r.normQty,
      r.period,
      r.employeeCount,
      r.totalNeed,
      r.issued,
      r.deficit,
    ]),
  });
}

export function exportProcurementCsv(rows: PpeProcurementRow[]) {
  const data = rows.map((r) => [
    r.position,
    r.ppeName,
    r.normQty,
    r.period,
    String(r.employeeCount),
    String(r.totalNeed),
    String(r.issued),
    String(r.deficit),
  ]);
  downloadUtf8Csv(PROCUREMENT_EXPORT.csvFilename, PROCUREMENT_EXPORT.csvHeader, data);
}
